import { z } from "zod";
import { Coordinates, Instant, Integer, SiteId, TripId, VehicleType } from "./common.js";
import { RosterCode } from "./drivers.js";
import { TrackedPositionSource } from "./trips.js";
// --- Mapa de la flota del proveedor (PP-51, ADR-025 §9) -------------------------
// Ultima posicion vigente de cada vehiculo de la empresa madre en todas sus
// plantas habilitadas, con el viaje vigente si lo tiene. Es la proyeccion del
// proveedor (su flota), distinta de la de la torre de la planta, que solo ve
// los moviles en viaje de esa planta (ADR-014 §6): no es un filtro de la misma.
// Mismo guard y conjunto de plantas que /provider/trips. Nada de pasajeros
// (ni nombres, ni paradas, ni coordenadas de paradas) ni de otra empresa madre.
// Se sigue al vehiculo, nunca a la persona: el conductor va solo por nombre de
// la nomina, como dato del viaje.
// en castellano: posicion vigente (fresh position), viaje vigente (current trip).
export const ProviderFleetPositionsQuery = z
    .object({})
    .strict()
    .openapi("ProviderFleetPositionsQuery", {
    description: "Sin parametros: ni planta, ni proveedor, ni organizacion (400). El conjunto de plantas sale de la base; el filtro de planta lo aplica el portal.",
});
export const ProviderFleetTripStatus = z
    .enum(["scheduled", "in_progress"])
    .openapi("ProviderFleetTripStatus", {
    description: "Estado del viaje vigente: en curso, o programado y asignado a ese vehiculo. Subconjunto de TripStatus.",
});
export const ProviderFleetTrip = z
    .object({
    siteId: SiteId,
    siteName: z.string().min(1).openapi({ description: "Nombre comercial de la planta del viaje." }),
    tripId: TripId,
    vehicleType: VehicleType.openapi({ description: "Servicio del viaje (auto, combi o colectivo)." }),
    status: ProviderFleetTripStatus,
    driverName: z.string().min(1).optional().openapi({
        description: "Nombre del conductor asignado, tal como esta en la nomina de la empresa madre. Ausente: el viaje no tiene conductor de la nomina.",
    }),
})
    .strict()
    .openapi("ProviderFleetTrip", {
    description: "Viaje vigente del vehiculo: el que tiene en curso o, si no, el proximo programado y asignado de hoy, en cualquiera de las plantas habilitadas. Sin pasajeros ni paradas.",
});
export const ProviderFleetPosition = z
    .object({
    siteId: SiteId.openapi({ description: "Planta donde se registro la posicion (la del viaje que la emitio)." }),
    coordinates: Coordinates,
    instant: Instant.openapi({ description: "Instante de la medicion, en ISO 8601 UTC. Siempre dentro de la ventana de vigencia de la respuesta." }),
    source: TrackedPositionSource,
})
    .strict()
    .openapi("ProviderFleetPosition", {
    description: "Ultima posicion vigente del vehiculo, de cualquier origen (app del conductor, telemetria o simulador): el mapa la dibuja igual.",
});
export const ProviderFleetVehicle = z
    .object({
    code: RosterCode,
    plate: z.string().min(1),
    type: VehicleType,
    position: ProviderFleetPosition,
    trip: ProviderFleetTrip.optional().openapi({ description: "Ausente: el vehiculo no tiene viaje en curso ni programado y asignado hoy." }),
})
    .strict()
    .openapi("ProviderFleetVehicle", {
    description: "Vehiculo de la nomina de la empresa madre, una sola vez aunque tenga filas en varias plantas, con su ultima posicion vigente. Sin ids globales.",
});
export const ProviderFleetSite = z
    .object({ siteId: SiteId, siteName: z.string().min(1), timezone: z.string().min(1).openapi({ description: "Zona IANA de la planta." }) })
    .strict()
    .openapi("ProviderFleetSite", { description: "Planta incluida en la lectura: una por habilitacion activa de la empresa madre, aunque no tenga posiciones." });
export const ProviderFleetPositionsResponse = z
    .object({
    freshnessMinutes: Integer.min(1).openapi({
        description: "Ventana de vigencia: solo entran posiciones medidas en los ultimos N minutos. Un vehiculo sin posicion en la ventana no viene.",
    }),
    sites: z.array(ProviderFleetSite),
    vehicles: z.array(ProviderFleetVehicle).openapi({ description: "Ordenados por codigo de movil." }),
})
    .strict()
    .openapi("ProviderFleetPositionsResponse", {
    description: "Flota del proveedor con posicion vigente en todas sus plantas habilitadas. Sin habilitaciones o sin posiciones: listas vacias, no error.",
});
//# sourceMappingURL=provider-fleet-positions.js.map